import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import {
  getAdminAffiliateSummary,
  getAdminAffiliateActions,
  runAdminAffiliateSync,
  AdminAffiliateAction,
  AdminAffiliateSummary,
} from '../features/admin/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import AdminCsvExportButton from '../features/admin/AdminCsvExportButton';

// Admin subpage: Omio affiliate conversions against clicks. Rows come from
// impact.com's Actions API through the backend's `affiliate_sync` job
// (services/affiliateSyncService.js; every 6 h in production via the jobs
// registry, backend/jobs). On load, when the job's last run is older than
// its interval (`job.stale`), the page runs it once before showing the
// numbers, so a backend without the scheduler still shows fresh data;
// "Sync now" runs it any time. Clicks are Impact's own monthly report.
// Doc: docs/integrations/omio-impact.md ("Conversions").

const Chip: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span style={{ background: bg, color: fg, borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

function stateChip(state: string) {
  const s = state.toUpperCase();
  if (s === 'APPROVED') return <Chip bg="rgba(34,197,94,0.15)" fg="#16a34a">Approved</Chip>;
  if (s === 'REVERSED') return <Chip bg="rgba(239,68,68,0.15)" fg="#dc2626">Reversed</Chip>;
  if (s === 'PENDING') return <Chip bg="rgba(245,158,11,0.18)" fg="#b45309">Pending</Chip>;
  return <Chip bg="rgba(148,163,184,0.18)" fg="#64748b">{state}</Chip>;
}

// A booking pays; an app install is a 0% event in the program terms.
function trackerChip(name: string | null, eventCode: string | null) {
  const install = /app/i.test(name || '') || eventCode === 'INSTALL';
  if (install) return <Chip bg="rgba(148,163,184,0.18)" fg="#64748b">{name || 'App install'}</Chip>;
  return <Chip bg="rgba(59,130,246,0.15)" fg="#2563eb">{name || 'Sale'}</Chip>;
}

const eur = (cents: number | null | undefined, currency?: string | null) =>
  cents == null ? '–' : `${(cents / 100).toFixed(2)} ${currency || 'EUR'}`;

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '–');

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--surface)', minWidth: 150 }}>
    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{value}</div>
    {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</div>}
  </div>
);

const AdminAffiliatePage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<AdminAffiliateSummary | null>(null);
  const [rows, setRows] = useState<AdminAffiliateAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // 'auto' while the stale-on-load refresh runs (one attempt per page load).
  const [autoRefresh, setAutoRefresh] = useState<'idle' | 'running' | 'done'>('idle');
  const autoTriedRef = useRef(false);

  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required. Redirecting to homepage.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const load = useCallback(async (): Promise<AdminAffiliateSummary | null> => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([getAdminAffiliateSummary(), getAdminAffiliateActions(300)]);
      setSummary(s);
      setRows(a);
      return s;
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Failed to load the affiliate data.', type: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const syncNow = useCallback(async (reason: 'admin' | 'stale_on_load' = 'admin') => {
    setSyncing(true);
    try {
      const r = await runAdminAffiliateSync(reason);
      if (r.skipped) {
        if (reason === 'admin') showToast({ title: 'Sync already running', message: 'Try again in a moment.', type: 'info' });
      } else if (reason === 'admin') {
        showToast({ title: 'Synced with impact.com', message: `${r.fetched} action(s) read, ${r.created} new, ${r.stateChanges} state change(s).`, type: 'success' });
      }
      await load();
    } catch (e: any) {
      showToast({ title: reason === 'admin' ? 'Sync failed' : 'Automatic refresh failed', message: e?.response?.data?.error || 'impact.com could not be read.', type: 'error', duration: 6000 });
    } finally {
      setSyncing(false);
    }
  }, [load, showToast]);

  // First load, then the stale-on-load refresh: once per page load, when the
  // job has credentials, is not running, and its last run is older than an
  // interval (or there was none since the backend started).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await load();
      if (cancelled || !s || autoTriedRef.current) return;
      autoTriedRef.current = true;
      if (s.configured && s.job && s.job.stale && !s.job.running) {
        setAutoRefresh('running');
        await syncNow('stale_on_load');
        if (!cancelled) setAutoRefresh('done');
      } else {
        setAutoRefresh('done');
      }
    })();
    return () => { cancelled = true; };
  }, [load, syncNow]);

  const totals = useMemo(() => {
    const by = (state: string) => summary?.byState.find((s) => s.state === state);
    const approved = by('APPROVED');
    const pending = by('PENDING');
    const reversed = by('REVERSED');
    const sales = (summary?.byTracker || []).filter((t) => !/app/i.test(t.actionTrackerName || '')).reduce((n, t) => n + t.count, 0);
    const installs = (summary?.byTracker || []).filter((t) => /app/i.test(t.actionTrackerName || '')).reduce((n, t) => n + t.count, 0);
    return {
      approvedPayout: approved?.payoutCents || 0,
      pendingPayout: pending?.payoutCents || 0,
      reversed: reversed?.count || 0,
      sales,
      installs,
    };
  }, [summary]);

  const clicks = summary?.clicks && !('error' in summary.clicks) ? summary.clicks : null;
  const clicksError = summary?.clicks && 'error' in summary.clicks ? summary.clicks.error : null;

  const columns: Column<AdminAffiliateAction>[] = useMemo(() => [
    { key: 'eventDate', header: 'Event', render: (r) => <span title={r.eventDate ? new Date(r.eventDate).toLocaleString() : ''}>{day(r.eventDate)}</span> },
    { key: 'actionTrackerName', header: 'Type', render: (r) => trackerChip(r.actionTrackerName, r.eventCode) },
    { key: 'state', header: 'State', render: (r) => stateChip(r.state) },
    { key: 'amountCents', header: 'Order', className: 'text-right', render: (r) => eur(r.amountCents, r.currency) },
    { key: 'payoutCents', header: 'Payout', className: 'text-right', render: (r) => <strong>{eur(r.payoutCents, r.currency)}</strong> },
    { key: 'customerCountry', header: 'Country', render: (r) => r.customerCountry || '–' },
    {
      key: 'journeyId', header: 'Journey',
      render: (r) => (r.journeyId
        ? <a href={`/journeys/${r.journeyId}`} target="_blank" rel="noreferrer" title={r.journeyId} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.journeyId.slice(0, 8)}…</a>
        : <span style={{ color: 'var(--text-muted)' }}>–</span>),
    },
    { key: 'referringDate', header: 'Click', render: (r) => day(r.referringDate) },
    { key: 'lockingDate', header: 'Locks', render: (r) => day(r.lockingDate) },
    { key: 'id', header: 'Impact id', render: (r) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.id}</span> },
  ], []);

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Omio affiliate</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate('/admin')}>Back to admin</Button>
          <AdminCsvExportButton table="affiliate-actions" label="Affiliate actions" />
          <Button variant="primary" onClick={() => syncNow('admin')} disabled={syncing || loading || summary?.configured === false}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
      </div>

      {summary && !summary.configured && (
        <div style={{ color: '#b45309', marginBottom: 12 }}>impact.com credentials are not set on this backend: nothing can be synced here.</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <Tile label="Clicks (Impact, deduplicated)" value={clicks ? clicks.totalClicks : (clicksError ? '–' : '…')} hint={clicks ? `since ${clicks.since}` : clicksError ? `Impact unreachable: ${clicksError}` : undefined} />
        <Tile label="Bookings" value={totals.sales} hint="Online Sale actions, any state" />
        <Tile label="App installs" value={totals.installs} hint="pay 0% (program terms)" />
        <Tile label="Approved payout" value={eur(totals.approvedPayout)} />
        <Tile label="Pending payout" value={eur(totals.pendingPayout)} hint={totals.reversed ? `${totals.reversed} reversed` : undefined} />
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
        {autoRefresh === 'running' && <span style={{ color: '#b45309' }}>Refreshing from impact.com (last sync older than {summary?.job ? Math.round(summary.job.intervalMs / 3600000) : 6} h)… </span>}
        {summary?.job?.enabled
          ? `Scheduled every ${Math.round(summary.job.intervalMs / 3600000)} h${summary.job.nextRunAt ? `, next ${new Date(summary.job.nextRunAt).toLocaleString()}` : ''}.`
          : `Scheduler off on this backend (${summary?.job?.disabledReason || 'job not registered'}); the page refreshes on load when the last sync is older than ${summary?.job ? Math.round(summary.job.intervalMs / 3600000) : 6} h, or with Sync now.`}
        {' '}
        {summary?.job?.lastRun
          ? `Last sync ${new Date(summary.job.lastRun.at).toLocaleString()} (${summary.job.lastRun.reason}): ${summary.job.lastRun.error ? `failed, ${summary.job.lastRun.error}` : `${summary.job.lastRun.result?.fetched ?? 0} read, ${summary.job.lastRun.result?.created ?? 0} new, ${summary.job.lastRun.result?.stateChanges ?? 0} state change(s)`}.`
          : 'No sync since this backend started.'}
      </div>

      {clicks && clicks.months.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Clicks by month (Impact)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {clicks.months.map((m) => (
              <div key={m.month || '?'} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: 'var(--text)' }}>
                <span style={{ color: 'var(--text-muted)' }}>{m.month}</span> · {m.clicks} click{m.clicks === 1 ? '' : 's'} · {m.actions} action{m.actions === 1 ? '' : 's'}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <Table columns={columns} data={rows} emptyMessage="No conversions yet. Bookings appear here within six hours of Omio recording them (or after Sync now)." />
      )}
    </div>
  );
};

export default AdminAffiliatePage;
